import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import {
  LayoutDashboard, Scale, Stamp, Files, ListChecks, CheckCircle2, Settings,
  Moon, Sun, LogOut, MoreHorizontal, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, testId: "nav-dashboard", primary: true },
  { to: "/adliye", label: "Adliye", icon: Scale, testId: "nav-adliye", primary: true },
  { to: "/turkpatent", label: "TÜRKPATENT", icon: Stamp, testId: "nav-turkpatent", primary: true },
  { to: "/diger", label: "Diğer", icon: Files, testId: "nav-other" },
  { to: "/isler", label: "Tüm İşler", icon: ListChecks, testId: "nav-tasks", primary: true },
  { to: "/tamamlananlar", label: "Tamamlananlar", icon: CheckCircle2, testId: "nav-completed" },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings, testId: "nav-settings" },
];

const navClass = (active) =>
  cn(
    "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-150",
    active
      ? "bg-navy text-white dark:bg-slate-100 dark:text-navy"
      : "text-muted-foreground hover:bg-muted hover:text-foreground"
  );

export const Layout = ({ children, onQuickCreate }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/giris");
  };

  const ThemeButton = () => (
    <Button
      variant="ghost"
      size="icon"
      data-testid="theme-toggle"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      aria-label="Tema"
    >
      {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </Button>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Masaüstü sol menü */}
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-[256px] flex-col border-r border-border bg-card lg:flex">
        <div className="px-6 pb-8 pt-7">
          <Link to="/" data-testid="brand-link">
            <img src="/shb-wordmark.jpg" alt="Sağlam Hukuk Bürosu" className="h-9 w-auto object-contain" />
          </Link>
          <p className="mt-4 text-[12px] font-semibold uppercase tracking-[1.2px] text-muted-foreground">
            Evrak Takip
          </p>
        </div>

        <nav className="flex flex-1 flex-col gap-1 px-3">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <Link key={to} to={to} data-testid={testId} className={navClass(pathname === to)}>
              <Icon className="h-[18px] w-[18px]" />
              {label}
            </Link>
          ))}
        </nav>

        <div className="border-t border-border px-6 py-5">
          <p className="text-sm font-semibold text-foreground">{user?.name || "Kullanıcı"}</p>
          <p className="text-xs text-muted-foreground">{user?.email}</p>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="mt-3 flex items-center gap-2 text-sm font-medium text-crimson transition-colors duration-150 hover:opacity-80"
          >
            <LogOut className="h-4 w-4" /> Çıkış Yap
          </button>
        </div>
      </aside>

      <div className="lg:pl-[256px]">
        {/* Üst başlık */}
        <header className="sticky top-0 z-30 border-b border-border bg-card">
          <div className="flex items-center gap-3 px-4 py-3 lg:px-8">
            <Link to="/" className="lg:hidden" data-testid="brand-link-mobile">
              <img src="/shb-wordmark.jpg" alt="Sağlam Hukuk Bürosu" className="h-7 w-auto object-contain" />
            </Link>
            <div className="ml-auto flex items-center gap-2">
              {onQuickCreate && (
                <Button
                  onClick={onQuickCreate}
                  data-testid="header-quick-create"
                  className="bg-crimson text-white hover:bg-crimson/90"
                >
                  <Plus className="h-4 w-4" /> Yeni Kayıt
                </Button>
              )}
              <ThemeButton />
            </div>
          </div>
        </header>

        <main className="px-4 pb-28 pt-6 lg:px-8 lg:pb-12 lg:pt-8">{children}</main>
      </div>

      {/* Mobil alt menü */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-5 border-t border-border bg-card lg:hidden">
        {NAV.filter((n) => n.primary).map(({ to, label, icon: Icon, testId }) => (
          <Link
            key={to}
            to={to}
            data-testid={`${testId}-mobile`}
            className={cn(
              "flex min-h-[62px] flex-col items-center justify-center gap-1 px-1 text-[11px] font-medium transition-colors duration-150",
              pathname === to ? "text-crimson" : "text-muted-foreground"
            )}
          >
            <Icon className="h-5 w-5" />
            <span className="truncate">{label}</span>
          </Link>
        ))}
        <button
          onClick={() => setMoreOpen(true)}
          data-testid="nav-more-mobile"
          className="flex min-h-[62px] flex-col items-center justify-center gap-1 px-1 text-[11px] font-medium text-muted-foreground"
        >
          <MoreHorizontal className="h-5 w-5" />
          Daha Fazla
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-xl" data-testid="more-menu">
          <SheetHeader>
            <SheetTitle className="font-display text-left text-xl">Daha Fazla</SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex flex-col gap-1">
            {NAV.filter((n) => !n.primary).map(({ to, label, icon: Icon, testId }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setMoreOpen(false)}
                data-testid={`${testId}-mobile`}
                className={navClass(pathname === to)}
              >
                <Icon className="h-[18px] w-[18px]" /> {label}
              </Link>
            ))}
            <button
              onClick={onLogout}
              data-testid="logout-btn-mobile"
              className="mt-2 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-crimson hover:bg-muted"
            >
              <LogOut className="h-[18px] w-[18px]" /> Çıkış Yap
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
